import { useEffect } from 'react';
import {
  Calendar,
  Clock,
  DollarSign,
  Hash,
  Home,
  Landmark,
  Loader2,
  ShieldCheck,
  User,
} from 'lucide-react';
import { useParams } from 'react-router-dom';

import { useGetNegotiationHistoryQuery } from '@/apis/extended/negotiation';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FieldRow } from './feildrow';
import { NegotiationStatusBadge } from './statusbadge';

export default function CounterProposalHistory() {
  const { contractId } = useParams();
  const { data, isLoading, isError, refetch } = useGetNegotiationHistoryQuery(
    contractId ?? '',
    {
      refetchOnMountOrArgChange: true,
      skip: !contractId,
      refetchOnFocus: true,
    }
  );

  const hasValue = (field: { value: string }): boolean => {
    if (field === null || field === undefined) return false;
    if (typeof field === 'object') {
      const value = field?.value ?? '';
      if (value === null || value === undefined) return false;
      return String(value).trim().length > 0;
    }
    return String(field).trim().length > 0;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--surface)] text-[var(--on-surface)]">
      <div className="container mx-auto px-6 py-8">
        {/* Professional Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-3">
            Counter Proposal History
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Track and review all negotiation proposals for this contract
          </p>
          {data?.negotiation?.CounterProposal && (
            <div className="mt-4 p-4 bg-[#043a93]/10 border border-[#043a93]/20 rounded-lg max-w-md mx-auto">
              <p className="text-[#043a93] font-medium">
                {data.negotiation.CounterProposal}
              </p>
            </div>
          )}
        </div>
        {/* Empty State */}
        {!isLoading &&
          !isError &&
          (!data?.negotiations || data?.negotiations?.length === 0) && (
            <div className="flex items-center justify-center w-full min-h-[50vh]">
              <Card className="w-full max-w-2xl bg-[var(--surface-container-lowest)] p-12 shadow-none">
                <CardHeader className="text-center">
                  <div className="w-20 h-20 bg-[#043a93]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Hash className="w-10 h-10 text-[#043a93]" />
                  </div>
                  <CardTitle className="text-2xl text-white mb-4">
                    No Negotiations Found
                  </CardTitle>
                  <CardDescription className="text-white text-lg">
                    Counter proposal history is not available for this contract
                    yet. Negotiations will appear here once they begin.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          )}

        {/* Negotiations Grid */}
        <div className="space-y-8">
          {data?.negotiations?.map((item: any, index: number) => (
            <Card
              key={item._id || index}
              className="w-full bg-[var(--surface-container-lowest)] shadow-none"
            >
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#043a93]/20 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        #{index + 1}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-300">
                        {item.timestamp &&
                          new Date(item.timestamp).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                      </p>
                    </div>
                  </div>
                  <span className="capitalize">
                    {NegotiationStatusBadge(
                      typeof item?.NegotiationStatus === 'object'
                        ? item?.NegotiationStatus?.value
                        : item?.NegotiationStatus
                    )}
                  </span>
                </div>
              </CardHeader>

              <div className="px-6 pb-6">
                {/* Metadata Section */}
                <div className="bg-[#043a93]/10 border border-[#043a93]/20 rounded-xl p-6 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {item.proposedBy && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-300" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Proposed By
                          </span>
                          <span className="text-white font-medium capitalize">
                            {typeof item.proposedBy === 'object'
                              ? item.proposedBy?.value
                              : item.proposedBy}
                          </span>
                        </div>
                      </div>
                    )}
                    {item.timestamp && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-gray-300" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Last Updated
                          </span>
                          <span className="text-white font-medium">
                            {new Date(item.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                    {item._id && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <Hash className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Negotiation ID
                          </span>
                          <span className="text-white font-mono text-sm">
                            {item._id}
                          </span>
                        </div>
                      </div>
                    )}
                    {item.contractId && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <Hash className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Contract ID
                          </span>
                          <span className="text-white font-mono text-sm">
                            {item.contractId}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Terms Section */}
                {(hasValue(item.offeredPrice) ||
                  hasValue(item.EarnestMoney) ||
                  hasValue(item.ClosingDate)) && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#043a93]" />
                      Financial Terms
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {hasValue(item.offeredPrice) && (
                        <FieldRow
                          icon={DollarSign}
                          label="Offered Price"
                          value={
                            typeof item.offeredPrice === 'object'
                              ? item.offeredPrice?.value
                              : item.offeredPrice
                          }
                          status={
                            typeof item.offeredPrice === 'object'
                              ? item.offeredPrice?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(item.EarnestMoney) && (
                        <FieldRow
                          icon={Landmark}
                          label="Earnest Money"
                          value={
                            typeof item.EarnestMoney === 'object'
                              ? item.EarnestMoney?.value
                              : item.EarnestMoney
                          }
                          status={
                            typeof item.EarnestMoney === 'object'
                              ? item.EarnestMoney?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(item.ClosingDate) && (
                        <FieldRow
                          icon={Calendar}
                          label="Closing Date"
                          value={
                            typeof item.ClosingDate === 'object'
                              ? item.ClosingDate?.value
                              : item.ClosingDate
                          }
                          status={
                            typeof item.ClosingDate === 'object'
                              ? item.ClosingDate?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Contingencies Section */}
                {(hasValue(item.AppraisalContingency) ||
                  hasValue(item.FinancingContingency) ||
                  hasValue(item.HomeInspection) ||
                  hasValue(item.postCloseOccupancy) ||
                  hasValue(item.Suggested) ||
                  hasValue(item.Contingent)) && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-[#043a93]" />
                      Contingencies & Additional Terms
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {hasValue(item.AppraisalContingency) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Appraisal Contingency"
                          value={
                            typeof item.AppraisalContingency === 'object'
                              ? item.AppraisalContingency?.value
                              : item.AppraisalContingency
                          }
                          status={
                            typeof item.AppraisalContingency === 'object'
                              ? item.AppraisalContingency?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(item.FinancingContingency) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Financing Contingency"
                          value={
                            typeof item.FinancingContingency === 'object'
                              ? item.FinancingContingency?.value
                              : item.FinancingContingency
                          }
                          status={
                            typeof item.FinancingContingency === 'object'
                              ? item.FinancingContingency?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(item.HomeInspection) && (
                        <FieldRow
                          icon={Home}
                          label="Home Inspection"
                          value={
                            typeof item.HomeInspection === 'object'
                              ? item.HomeInspection?.value
                              : item.HomeInspection
                          }
                          status={
                            typeof item.HomeInspection === 'object'
                              ? item.HomeInspection?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(item.postCloseOccupancy) && (
                        <FieldRow
                          icon={Home}
                          label="Post Close Occupancy"
                          value={
                            typeof item.postCloseOccupancy === 'object'
                              ? item.postCloseOccupancy?.value
                              : item.postCloseOccupancy
                          }
                          status={
                            typeof item.postCloseOccupancy === 'object'
                              ? item.postCloseOccupancy?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}

                      {hasValue(item.Suggested) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Suggested"
                          value={
                            typeof item.Suggested === 'object'
                              ? item.Suggested?.value
                              : item.Suggested
                          }
                          status={
                            typeof item.Suggested === 'object'
                              ? item.Suggested?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(item.Contingent) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Contingent"
                          value={
                            typeof item.Contingent === 'object'
                              ? item.Contingent?.value
                              : item.Contingent
                          }
                          status={
                            typeof item.Contingent === 'object'
                              ? item.Contingent?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {isLoading && (
                  <div className="flex justify-center items-center py-12">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-6 h-6 animate-spin text-[#043a93]" />
                      <span className="text-gray-300 font-medium">
                        Loading proposal details...
                      </span>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {isError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                        <Hash className="w-5 h-5 text-red-300" />
                      </div>
                      <span className="text-red-300 font-medium">
                        Failed to load counter proposal details
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refetch}
                      className="bg-red-500/20 border-red-500/30 text-red-300 hover:bg-red-500/30 hover:border-red-500/40"
                    >
                      <Loader2 className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
